import { evaluateSaleLocation, saleLocationPolicy } from "./saleCheckoutPolicy";

const LOCATION_CACHE_MS = 30_000;
const LOCATION_FUTURE_TOLERANCE_MS = 30_000;
let cachedLocation = null;
let pendingLocation = null;
let permissionStatus = null;
let locationWatchId = null;

export class BrowserLocationError extends Error {
  constructor(code, message) {
    super(message);
    this.name = "BrowserLocationError";
    this.code = code;
  }
}

async function checkPermission() {
  if (!navigator.permissions?.query) return;
  permissionStatus ??= await navigator.permissions.query({ name: "geolocation" });
  permissionStatus.onchange = () => {
    if (permissionStatus.state !== "granted") {
      cachedLocation = null;
    }
  };
  if (permissionStatus.state === "denied") {
    cachedLocation = null;
    throw new Error("Location permission is blocked. Enable it in browser site settings.");
  }
}

function locationError(error, highAccuracy) {
  if (error?.code === 1) return "Location permission was not granted.";
  if (error?.code === 2) return "Your location is unavailable. Turn on location services and try again.";
  if (error?.code === 3) {
    return highAccuracy
      ? "A precise location could not be obtained in time. Try again outdoors."
      : "Your location could not be obtained in time. Check Chrome and macOS Location Services, then try again.";
  }
  return "Your location could not be captured.";
}

function roundCoordinate(value) {
  return Math.round(value * 1_000_000) / 1_000_000;
}

export function normalizeBrowserPosition(position, observedAt = Date.now()) {
  const accuracy = Number(position?.coords?.accuracy);
  const capturedAt = Number(position?.timestamp);
  const latitude = Number(position?.coords?.latitude);
  const longitude = Number(position?.coords?.longitude);

  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90 ||
      !Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
    throw new BrowserLocationError(
      "invalid_coordinates",
      "The browser returned an invalid location. Refresh the page and try again.",
    );
  }
  if (!Number.isFinite(accuracy) || accuracy <= 0) {
    throw new BrowserLocationError(
      "invalid_accuracy",
      "The browser did not provide location accuracy. Try again near a window.",
    );
  }
  if (!Number.isFinite(capturedAt)) {
    throw new BrowserLocationError(
      "invalid_timestamp",
      "The browser did not provide a location timestamp. Refresh the page and try again.",
    );
  }
  if (
    capturedAt < observedAt - LOCATION_CACHE_MS ||
    capturedAt > observedAt + LOCATION_FUTURE_TOLERANCE_MS
  ) {
    throw new BrowserLocationError(
      "stale",
      "Chrome returned an old location. Check macOS Location Services, then try again.",
    );
  }

  return {
    accuracyMeters: accuracy,
    capturedAt: new Date(capturedAt).toISOString(),
    latitude: roundCoordinate(latitude),
    longitude: roundCoordinate(longitude),
    mocked: false,
    permissionAccuracy: "browser",
    source: "web",
  };
}

function acquireBrowserPosition({ highAccuracy, maximumAge, timeoutMs }) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => reject(new BrowserLocationError("unavailable", locationError(error, highAccuracy))),
      { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge },
    );
  });
}

async function acquireNormalizedLocation({ fresh, highAccuracy, timeoutMs }) {
  const maximumAge = fresh ? 0 : LOCATION_CACHE_MS;
  const options = { highAccuracy, maximumAge, timeoutMs };
  try {
    return normalizeBrowserPosition(await acquireBrowserPosition(options));
  } catch (error) {
    if (!(error instanceof BrowserLocationError) || error.code !== "stale") throw error;
    // Some desktop providers return their last network fix even when maximumAge
    // is zero. Give Chrome one explicit second acquisition before failing locally.
    return normalizeBrowserPosition(await acquireBrowserPosition({ ...options, maximumAge: 0 }));
  }
}

export function clearBrowserLocationCache() {
  cachedLocation = null;
}

export function getLatestBrowserLocation() {
  return cachedLocation?.value ?? null;
}

export function startBrowserLocationTracking() {
  if (
    locationWatchId !== null ||
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    !navigator.geolocation ||
    (!window.isSecureContext && window.location.hostname !== "localhost")
  ) {
    return;
  }

  locationWatchId = navigator.geolocation.watchPosition(
    (position) => {
      try {
        const value = normalizeBrowserPosition(position);
        cachedLocation = { cachedAt: Date.now(), value };
      } catch {
        // Invalid readings are ignored; operations remain available.
      }
    },
    () => {
      // Permission/provider failures are observational and never block work.
      // Release the failed watch so focus/visibility can start it again after
      // the user changes browser or operating-system location settings.
      if (locationWatchId !== null) {
        navigator.geolocation.clearWatch(locationWatchId);
        locationWatchId = null;
      }
    },
    { enableHighAccuracy: false, maximumAge: LOCATION_CACHE_MS, timeout: 20000 },
  );
}

export function stopBrowserLocationTracking() {
  if (locationWatchId === null || typeof navigator === "undefined" || !navigator.geolocation) return;
  navigator.geolocation.clearWatch(locationWatchId);
  locationWatchId = null;
}

export async function requireBrowserLocation({ fresh = false, highAccuracy = true, timeoutMs = 15000 } = {}) {
  if (typeof window === "undefined" || typeof navigator === "undefined" || !navigator.geolocation) {
    throw new Error("This browser does not support the location required by Shopbook.");
  }
  if (!window.isSecureContext && window.location.hostname !== "localhost") {
    throw new Error("Location requires a secure HTTPS connection.");
  }
  await checkPermission();
  if (!fresh && cachedLocation && Date.now() - cachedLocation.cachedAt <= LOCATION_CACHE_MS) {
    return cachedLocation.value;
  }
  if (pendingLocation) return pendingLocation;

  pendingLocation = acquireNormalizedLocation({ fresh, highAccuracy, timeoutMs }).then((value) => {
    cachedLocation = { cachedAt: Date.now(), value };
    return value;
  });
  try {
    return await pendingLocation;
  } finally {
    pendingLocation = null;
  }
}

export async function requirePreciseBrowserLocation({ fresh = false } = {}) {
  const cachedOrCurrent = await requireBrowserLocation({ fresh });
  try {
    return assertTrustedSaleLocation(cachedOrCurrent);
  } catch {
    cachedLocation = null;
    return assertTrustedSaleLocation(await requireBrowserLocation({ fresh: true }));
  }
}

export function requireRequiredBrowserLocation({ fresh = true } = {}) {
  // Login only needs proof that browser location is enabled and usable. Asking
  // desktop Chrome for GPS-grade precision can time out even when location is
  // allowed, so accept its network/Wi-Fi fix regardless of accuracy.
  return requireBrowserLocation({ fresh, highAccuracy: false, timeoutMs: 12000 });
}

export class SaleLocationError extends Error {
  constructor(code, location) {
    super(code === "mocked" ? "Mocked sale location." : "Imprecise sale location.");
    this.name = "SaleLocationError";
    this.code = code;
    this.accuracyMeters = location?.accuracyMeters;
  }
}

function assertTrustedSaleLocation(location) {
  const failure = evaluateSaleLocation(location);
  if (failure) throw new SaleLocationError(failure, location);
  return location;
}

export async function warmRequiredSaleLocation() {
  if (
    cachedLocation &&
    Date.now() - cachedLocation.cachedAt <= saleLocationPolicy.prewarmMaxAgeMs
  ) {
    try {
      assertTrustedSaleLocation(cachedLocation.value);
      return;
    } catch {
      cachedLocation = null;
    }
  }

  cachedLocation = null;
  assertTrustedSaleLocation(await requireBrowserLocation({ fresh: true }));
}
